import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_NODE_URL, LIST_ALL_STORE_ORDER_LIST_NEW } from "../../../Constants/Config";
import { getOrderListCount } from "../Orders/inStoreOrderSlice";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";

const initialState = {
    loading: false,
    inStoreOrderData: [],
    // fetchKey of the request that produced inStoreOrderData (provenance tag so the
    // UI can prove the rendered list matches the active filter — see component merge effect)
    inStoreOrderDataKey: null,
    allOrdersCount: 0,
    refundedOnlyCount: 0,
    /** Legacy Orders page uses this for pagination (separate count API). */
    OrderListCount: 0,
    successMessage: "",
    error: "",
    latestOrderDataRequestId: null,
    latestOrderDataCountRequestId: null,
};
let OrderDataAbortController = null;
let RefundedCountAbortController = null;
let ExportAbortController = null;

/** Fetches order list for Excel export only — does not update Redux listing state. */
export const fetchInStoreOrderListForExport = async (data) => {
    if (ExportAbortController) {
        ExportAbortController.abort();
    }
    ExportAbortController = new AbortController();
    const signal = ExportAbortController.signal;

    const { token, ...newData } = data;
    const response = await axios.post(
        BASE_NODE_URL + LIST_ALL_STORE_ORDER_LIST_NEW,
        newData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
            },
            signal,
        }
    );
    if (response.data.status !== true) {
        throw new Error(response.data?.message ?? "Export request failed");
    }
    const orderData = response.data.order_data;
    return Array.isArray(orderData) ? orderData : orderData?.list ?? [];
};

const pickNumericCount = (...candidates) => {
    for (const v of candidates) {
        if (v == null || v === "") continue;
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
    }
    return undefined;
};

/** Fetches refunded count only (separate controller, does not update list) - for initial load */
export const fetchRefundedCountOnly = createAsyncThunk(
    "inStoreOrder/fetchRefundedCountOnly",
    async (data, { rejectWithValue }) => {
        if (RefundedCountAbortController) RefundedCountAbortController.abort();
        RefundedCountAbortController = new AbortController();
        const { token, ...newData } = data;
        try {
            const response = await axios.post(
                BASE_NODE_URL + LIST_ALL_STORE_ORDER_LIST_NEW,
                { ...newData, list_type: "refunded_only", perpage: 1, page: "1" },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                    signal: RefundedCountAbortController.signal,
                }
            );
            if (response.data.status === true) {
                const refundedOnlyCount = response.data.refunded_only_count != null ? Number(response.data.refunded_only_count) : 0;
                return { refundedOnlyCount };
            }
            return rejectWithValue({ message: response.data?.message ?? "Request failed" });
        } catch (error) {
            if (error.name === "AbortError") return rejectWithValue({ message: "Request canceled" });
            return rejectWithValue({ message: error.message });
        }
    }
);

// Generate pening , fulfilled and rejected action type
export const fetchInStoreOrderData = createAsyncThunk(
    "inStoreOrder/fetchInStoreOrderData.",
    async (data, { rejectWithValue, requestId }) => {
        if (OrderDataAbortController) {
            OrderDataAbortController.abort();
        }

        OrderDataAbortController = new AbortController();
        const signal = OrderDataAbortController.signal;

        // __fetchKey is a client-only provenance tag — strip it so it is never sent to the API
        const { token, __fetchKey = null, ...newData } = data;
        try {
            const response = await axios.post(
                BASE_NODE_URL + LIST_ALL_STORE_ORDER_LIST_NEW,
                newData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                    signal,
                }
            );
            if (response.data.status === true) {
                const orderData = response.data.order_data;
                const normalized = Array.isArray(orderData) ? orderData : orderData?.list ?? [];
                const nested =
                    orderData && typeof orderData === "object" && !Array.isArray(orderData)
                        ? orderData
                        : null;
                const allOrdersCount = pickNumericCount(
                    response.data.all_orders_count,
                    response.data.all_order_count,
                    response.data.allOrdersCount,
                    nested?.all_orders_count,
                    nested?.total_count,
                    nested?.total
                );
                const refundedOnlyCount = pickNumericCount(
                    response.data.refunded_only_count,
                    response.data.refundedOnlyCount,
                    nested?.refunded_only_count,
                    nested?.refundedOnlyCount
                );
                const listType = newData?.list_type ?? "all_orders";
                return { requestId, data: normalized, allOrdersCount, refundedOnlyCount, listType, fetchKey: __fetchKey };
            }
            return rejectWithValue({
                message: response.data?.message ?? "Request failed",
                requestId,
                fetchKey: __fetchKey,
            });
        } catch (error) {
            if (error.name === "AbortError") {
                return rejectWithValue({ message: "Request canceled", requestId, fetchKey: __fetchKey });
            }
            const customError = {
                message: toThunkRejectValue(error).message,
                status: toThunkRejectValue(error).status,
                data: error.response ? error.response.data : null,
                requestId,
                fetchKey: __fetchKey,
            };
            return rejectWithValue(customError);
        }
    }
);

const inStoreOrderSlice = createSlice({
    name: "inStoreOrder",
    initialState,
    reducers: {
        updateInstoreOrderDataState: (state) => {
            state.inStoreOrderData = [];
            state.inStoreOrderDataKey = null;
            state.allOrdersCount = 0;
            state.refundedOnlyCount = 0;
            state.OrderListCount = 0;
            state.loading = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchInStoreOrderData.pending, (state, action) => {
            state.loading = true;
            state.latestOrderDataRequestId = action.meta.requestId;
        });
        builder.addCase(fetchInStoreOrderData.fulfilled, (state, action) => {
            if (state.latestOrderDataRequestId !== action.meta.requestId) {
                return;
            }
            state.loading = false;
            state.inStoreOrderData = action.payload.data;
            state.inStoreOrderDataKey = action.payload.fetchKey ?? null;
            const listType = action.payload.listType ?? "all_orders";
            // Total count for the listing we asked for (all vs refunded use different response fields)
            if (listType === "all_orders" && action.payload.allOrdersCount != null) {
                state.allOrdersCount = action.payload.allOrdersCount;
            }
            // Refunded total: API returns this on both list_type payloads; keep in sync on all_orders loads
            if (action.payload.refundedOnlyCount != null) {
                state.refundedOnlyCount = action.payload.refundedOnlyCount;
            }
            state.error = "";
        });
        builder.addCase(fetchInStoreOrderData.rejected, (state, action) => {
            if (state.latestOrderDataRequestId !== action.meta.requestId) {
                return;
            }
            if (action.payload?.message === "Request canceled") {
                return;
            }
            state.loading = false;
            state.inStoreOrderData = [];
            // Tag with the failed request's fetchKey so the component accepts the cleared
            // list for the active filter (a genuine error empties the view, as before)
            state.inStoreOrderDataKey = action.payload?.fetchKey ?? null;
            state.error = action.error?.message ?? action.payload?.message ?? "";
        });
        builder.addCase(fetchRefundedCountOnly.fulfilled, (state, action) => {
            state.refundedOnlyCount = action.payload?.refundedOnlyCount ?? 0;
        });
        // Legacy Orders > In-Store table uses getOrderListCount + OrderListCount
        builder.addCase(getOrderListCount.pending, (state, action) => {
            state.latestOrderDataCountRequestId = action.meta.requestId;
        });
        builder.addCase(getOrderListCount.fulfilled, (state, action) => {
            if (state.latestOrderDataCountRequestId !== action.meta.requestId) {
                return;
            }
            state.OrderListCount = action.payload.data;
        });
        builder.addCase(getOrderListCount.rejected, (state, action) => {
            if (state.latestOrderDataCountRequestId !== action.meta.requestId) {
                return;
            }
            if (action.payload?.message === "Request canceled") {
                return;
            }
            state.OrderListCount = 0;
        });
    },
});
export const { updateInstoreOrderDataState } = inStoreOrderSlice.actions;
export default inStoreOrderSlice.reducer;
